import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {Object} DmQuickViewTriggerRefs
 * @property {HTMLButtonElement} [triggerBtn]
 */

/**
 * Trigger del Quick View. Fetcha el PDP y muestra el contenido en el modal dm-quick-view-dialog.
 * Uso: <dm-quick-view-trigger data-product-url="/products/handle">
 *
 * @extends {Component<DmQuickViewTriggerRefs>}
 */
class DmQuickViewTrigger extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartAbortController = new AbortController();

  get #productUrl() {
    return this.dataset.productUrl || '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#cartAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#cartAbortController.abort();
  }

  /** Limpia la caché cuando el carrito cambia (precios / stock pueden cambiar). */
  #onCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Manejador del click en el botón Quick View.
   * Declarado como propiedad para que `on:click="/openQuickView"` funcione.
   * @param {Event} event
   */
  openQuickView = async (event) => {
    event.preventDefault();

    const url = this.#productUrl;
    if (!url) return;

    let productGrid = this.#cachedContent.get(url);

    if (!productGrid) {
      const html = await this.#fetchPage(url);
      if (!html) return;

      const grid = html.querySelector('[data-product-grid-content]');
      if (grid) {
        productGrid = /** @type {Element} */ (grid.cloneNode(true));
        this.#cachedContent.set(url, productGrid);
      }
    }

    if (!productGrid) return;

    this.#morphModal(/** @type {Element} */ (productGrid.cloneNode(true)));
    this.#openModal();
  };

  /**
   * @param {Element} productGrid
   */
  #morphModal(productGrid) {
    const modalContent = document.getElementById('dm-quick-view-modal-content');
    if (!modalContent) return;
    morph(modalContent, productGrid);
    // Scripts inserted via clone/morph never execute, so the product components
    // (slideshow, variant pickers, bulk-options-picker, bulk-order-table) never
    // register or hydrate on pages where they aren't already loaded (e.g. collections).
    // Re-create the script tags to run their `import '@theme/...'` and hydrate the markup.
    this.#activateScripts(modalContent);
  }

  /**
   * Re-executes the script tags inside a freshly-injected container.
   * @param {HTMLElement} container
   */
  #activateScripts(container) {
    for (const oldScript of container.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const attribute of oldScript.attributes) {
        script.setAttribute(attribute.name, attribute.value);
      }
      if (!oldScript.src) script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    }
  }

  #openModal() {
    const dialog = document.getElementById('dm-quick-view-dialog');
    if (!(dialog instanceof DmQuickViewDialog)) return;

    this.toggleAttribute('stay-visible', true);
    dialog.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });

    dialog.showDialog();
  }

  /**
   * Fetcha la página del producto y devuelve el Document parseado.
   * @param {string} url
   * @returns {Promise<Document | null>}
   */
  async #fetchPage(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
      return null;
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('dm-quick-view-trigger')) {
  customElements.define('dm-quick-view-trigger', DmQuickViewTrigger);
}

/**
 * Dialog del Quick View. Extiende DialogComponent para heredar
 * showDialog / closeDialog / scroll-lock / backdrop click / Escape.
 */
class DmQuickViewDialog extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** Cierra el modal cuando se añade al carrito exitosamente. */
  #onCartUpdate = (/** @type {CustomEvent} */ event) => {
    if (event.detail?.data?.didError) return;
    this.closeDialog();
  };
}

if (!customElements.get('dm-quick-view-dialog')) {
  customElements.define('dm-quick-view-dialog', DmQuickViewDialog);
}
